import { useState, useEffect } from "react";
import axios from "axios";
import { Layers, Search, Filter, Zap, RefreshCw, AlertTriangle, CheckCircle, Loader2, Image, ChevronDown, ChevronUp, Volume2, Download, FileText } from "lucide-react";
import EpisodeAssetCard from "./EpisodeAssetCard";
import MusicManager from "./MusicManager";
import VoiceProfileManager from "./VoiceProfileManager";
import VoiceAssignmentPanel from "./VoiceAssignmentPanel";
import SfxPreviewPlayer from "./SfxPreviewPlayer";
import VoiceLab from "./VoiceLab";
import AudioMixerSection from "./AudioMixerSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  script: { label: "Script", color: "bg-gray-600", icon: "S" },
  assets: { label: "Assets", color: "bg-yellow-600", icon: "A" },
  recording: { label: "Recording", color: "bg-blue-600", icon: "R" },
  mixing: { label: "Mixing", color: "bg-purple-600", icon: "M" },
  complete: { label: "Complete", color: "bg-green-600", icon: "C" }
};

const AssetManager = ({ embedded = false, seriesOverride = null }) => {
  // Core filters
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [seriesFilter, setSeriesFilter] = useState(seriesOverride || "the-red-ink-chronicles");
  const [seasonFilter, setSeasonFilter] = useState("1");

  // Progress & bulk actions
  const [expansionStatus, setExpansionStatus] = useState(null);
  const [thumbnailStatus, setThumbnailStatus] = useState(null);
  const [seasonProgress, setSeasonProgress] = useState([]);
  const [expanding, setExpanding] = useState(false);
  const [generatingThumbnails, setGeneratingThumbnails] = useState(false);
  const [currentExpanding, setCurrentExpanding] = useState(null);
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [expandProgress, setExpandProgress] = useState({ done: 0, total: 0 });
  const [thumbnailProgress, setThumbnailProgress] = useState({ done: 0, total: 0 });

  // Revision scanner
  const [revisionScan, setRevisionScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [revisingId, setRevisingId] = useState(null);
  const [showRevisionPanel, setShowRevisionPanel] = useState(false);
  const [batchRevising, setBatchRevising] = useState(false);
  const [batchReviseProgress, setBatchReviseProgress] = useState({ done: 0, total: 0 });

  // Collapsible sections
  const [episodesCollapsed, setEpisodesCollapsed] = useState(false);
  const [voiceAssignCollapsed, setVoiceAssignCollapsed] = useState(false);
  const [voiceAssignEpId, setVoiceAssignEpId] = useState(null);
  const [batchAssigning, setBatchAssigning] = useState(false);

  // SFX Overview
  const [sfxData, setSfxData] = useState(null);
  const [sfxLoading, setSfxLoading] = useState(false);
  const [showSfxPanel, setShowSfxPanel] = useState(false);

  // SFX Scraper
  const [scrapeJob, setScrapeJob] = useState(null);
  const [scrapeRunning, setScrapeRunning] = useState(false);
  const [sfxDownloads, setSfxDownloads] = useState([]);
  const [showSfxScraper, setShowSfxScraper] = useState(false);

  // Batch TTS
  const [batchTTSJob, setBatchTTSJob] = useState(null);
  const [batchTTSRunning, setBatchTTSRunning] = useState(false);
  const [showBatchTTS, setShowBatchTTS] = useState(false);

  // ==================== DATA FETCHING ====================
  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/episodes?season=${seasonFilter}&series=${seriesFilter}&limit=150`);
      setEpisodes(res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchExpansionStatus = async () => {
    try { const res = await axios.get(`${API}/episodes/expansion-status?series=${seriesFilter}`); setExpansionStatus(res.data); }
    catch (e) { console.error(e); }
  };

  const fetchThumbnailStatus = async () => {
    try { const res = await axios.get(`${API}/episodes/thumbnail-status?series=${seriesFilter}`); setThumbnailStatus(res.data); }
    catch (e) { console.error(e); }
  };

  const fetchSeasonProgress = async () => {
    try { const res = await axios.get(`${API}/episodes/season-progress?series=${seriesFilter}`); setSeasonProgress(res.data); }
    catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchEpisodes();
    fetchExpansionStatus();
    fetchThumbnailStatus();
    fetchSeasonProgress();
    const interval = setInterval(() => {
      if (expanding || generatingThumbnails) { fetchExpansionStatus(); fetchThumbnailStatus(); }
    }, 10000);
    return () => clearInterval(interval);
  }, [expanding, generatingThumbnails, seasonFilter, seriesFilter]);

  useEffect(() => {
    if (seriesOverride && seriesOverride !== seriesFilter) {
      setSeriesFilter(seriesOverride);
      setSeasonFilter("1");
      setEpisodes([]);
      setRevisionScan(null);
    }
  }, [seriesOverride]);

  // ==================== BULK ACTIONS ====================
  const continueAllExpansions = async () => {
    setExpanding(true);
    const toExpand = episodes.filter(ep => !ep.full_story);
    setExpandProgress({ done: 0, total: toExpand.length });
    for (let i = 0; i < toExpand.length; i++) {
      const ep = toExpand[i];
      setCurrentExpanding(`S${ep.season}E${ep.episode_number}: ${ep.title}`);
      try { await axios.post(`${API}/episodes/${ep.id}/expand`); } catch (e) { console.error(`Failed: ${ep.title}`, e); }
      setExpandProgress({ done: i + 1, total: toExpand.length });
      await new Promise(r => setTimeout(r, 500));
    }
    setExpanding(false);
    setCurrentExpanding(null);
    fetchEpisodes();
    fetchExpansionStatus();
  };

  const generateAllThumbnails = async () => {
    setGeneratingThumbnails(true);
    setThumbnailProgress({ done: 0, total: 0 });
    try {
      const res = await axios.post(`${API}/episodes/generate-thumbnails-batch?series=${seriesFilter}&limit=50`);
      const jobId = res.data.job_id;
      if (!jobId) {
        alert(res.data.message || "All episodes already have thumbnails");
        setGeneratingThumbnails(false);
        return;
      }
      setThumbnailProgress({ done: 0, total: res.data.total });
      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/episodes/thumbnail-status/${jobId}`);
          const job = statusRes.data;
          setThumbnailProgress({ done: job.done || 0, total: job.total || 0 });
          setCurrentThumbnail(job.current || "");
          if (job.status === "done" || job.status === "error") {
            clearInterval(pollInterval);
            setGeneratingThumbnails(false);
            setCurrentThumbnail(null);
            fetchEpisodes();
            fetchThumbnailStatus();
          }
        } catch (e) { console.error(e); }
      }, 5000);
    } catch (e) {
      console.error(e);
      alert("Failed to start thumbnail generation");
      setGeneratingThumbnails(false);
    }
  };

  // ==================== REVISION SCANNER ====================
  const runRevisionScan = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/episodes/revision-scan?season=${seasonFilter}&series=${seriesFilter}`);
      setRevisionScan(res.data);
      setShowRevisionPanel(true);
    } catch (e) { console.error(e); }
    finally { setScanning(false); }
  };

  const reviseEpisode = async (episodeId) => {
    setRevisingId(episodeId);
    try {
      await axios.post(`${API}/episodes/${episodeId}/revise`);
      const res = await axios.get(`${API}/episodes/revision-scan?season=${seasonFilter}&series=${seriesFilter}`);
      setRevisionScan(res.data);
      fetchEpisodes();
    } catch (e) { console.error(e); alert("Revision failed."); }
    finally { setRevisingId(null); }
  };

  const batchReviseAll = async () => {
    if (!revisionScan || revisionScan.flagged_episodes.length === 0) return;
    // Only revise episodes with story issues
    const storyFlagged = revisionScan.flagged_episodes.filter(ep => ep.has_story_issues);
    if (storyFlagged.length === 0) {
      alert("No story issues found — all episodes have scripts. Remaining issues are asset-related (thumbnails, loglines).");
      return;
    }
    setBatchRevising(true);
    setBatchReviseProgress({ done: 0, total: 0, saved: 0 });

    try {
      const res = await axios.post(`${API}/episodes/batch-revise`, {
        series: seriesFilter, season: parseInt(seasonFilter)
      });
      const jobId = res.data.job_id;

      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/episodes/revise-status/${jobId}`);
          const job = statusRes.data;
          setBatchReviseProgress({ done: job.done || 0, total: job.total || 0, current: job.current || "", saved: job.saved || 0 });

          if (job.status === "done") {
            clearInterval(poll);
            setBatchRevising(false);
            setRevisingId(null);
            alert(`Batch complete! ${job.saved || job.done} episodes written/revised.`);
            const scanRes = await axios.get(`${API}/episodes/revision-scan?season=${seasonFilter}&series=${seriesFilter}`);
            setRevisionScan(scanRes.data);
            fetchEpisodes();
            fetchExpansionStatus();
          } else if (job.status === "error") {
            clearInterval(poll);
            setBatchRevising(false);
            alert("Batch revision failed: " + (job.error || "Unknown error"));
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 5000);

    } catch (e) {
      console.error(e);
      setBatchRevising(false);
      alert("Failed to start batch revision: " + (e.response?.data?.detail || e.message));
    }
  };

  // ==================== DERIVED DATA ====================
  const filteredEpisodes = episodes.filter(ep => {
    const matchesSearch = ep.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `S${ep.season}E${ep.episode_number}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || ep.production_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: episodes.length,
    complete: episodes.filter(ep => ep.production_status === "complete").length,
    withAssets: episodes.filter(ep => ep.narrator_track_url || ep.music_bed_url || ep.sfx_url).length,
    withThumbnails: episodes.filter(ep => ep.thumbnail_url).length,
    expanded: episodes.filter(ep => ep.full_story).length
  };

  const bulkExportStories = () => {
    const expandedEps = filteredEpisodes.filter(ep => ep.full_story);
    const content = expandedEps.map(ep => `# ${ep.title}\n## Season ${ep.season}, Episode ${ep.episode_number}\n\n### LOGLINE\n${ep.logline}\n\n### SUMMARY\n${ep.summary}\n\n### CHARACTERS\n${ep.characters?.join(", ") || "N/A"}\n\n### LOCATIONS\n${ep.locations?.join(", ") || "N/A"}\n\n### FULL STORY\n${ep.full_story || ep.script || "No story available"}\n\n---\n`).join("\n\n");
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `RedInkStudios_FullStories_${new Date().toISOString().split("T")[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className={embedded ? "py-12 flex items-center justify-center" : "min-h-screen pt-24 flex items-center justify-center"}>
        <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={embedded ? "pb-12" : "min-h-screen pt-24 pb-32 px-6"} data-testid="asset-manager-page">
      <div className={embedded ? "" : "max-w-5xl mx-auto"}>
        {!embedded && (
          <header className="mb-8">
            <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-2">PRE-PRODUCTION</p>
            <h1 className="font-cinzel text-4xl font-bold mb-4">Asset Manager</h1>
            <p className="text-gray-400">Upload thumbnails, narrator tracks, music beds, and SFX for your episodes.</p>
          </header>
        )}

        {/* ==================== SERIES SELECTOR ==================== */}
        {!embedded && (
          <div className="flex justify-center gap-3 mb-8" data-testid="series-selector">
            {[
              { slug: "the-red-ink-chronicles", label: "The Red Ink Chronicles", badge: "PG-13" },
              { slug: "the-notarys-shadow", label: "The Notary's Shadow", badge: "17+" },
              { slug: "signed-sealed-betrayed", label: "Signed, Sealed, Betrayed", badge: "17+" }
            ].map(s => (
              <button key={s.slug} onClick={() => { setSeriesFilter(s.slug); setSeasonFilter("1"); }}
                className={`px-5 py-3 rounded-lg font-cinzel text-sm font-bold transition-all ${seriesFilter === s.slug ? "bg-red-600 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"}`}
                data-testid={`series-${s.slug}-btn`}>
                {s.label} <span className="text-[10px] ml-1 opacity-60">{s.badge}</span>
              </button>
            ))}
          </div>
        )}

        {/* ==================== SEASON PROGRESS DASHBOARD ==================== */}
        {seasonProgress.length > 0 && (
          <div className="mb-10" data-testid="season-progress-dashboard">
            <div className="flex items-center gap-3 mb-4">
              <Layers size={20} className="text-red-500" />
              <h2 className="font-anton text-lg tracking-wider">PRODUCTION OVERVIEW</h2>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {seasonProgress.map(s => {
                const overallPct = Math.round((s.expand_pct + s.thumb_pct + s.audio_pct) / 3);
                const isActive = seasonFilter === String(s.season);
                return (
                  <div key={s.season} onClick={() => setSeasonFilter(String(s.season))}
                    className={`relative overflow-hidden border rounded-lg p-4 cursor-pointer transition-all hover:scale-[1.02] w-[140px] ${isActive ? "border-red-500/50 bg-red-950/20" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}
                    data-testid={`season-progress-${s.season}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-anton text-xs tracking-wider text-gray-400">S{s.season}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">{s.total}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${s.expand_pct}%` }} /></div>
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-purple-500 rounded-full" style={{ width: `${s.thumb_pct}%` }} /></div>
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${s.audio_pct}%` }} /></div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className={`text-xs font-mono font-bold ${overallPct >= 80 ? "text-green-400" : overallPct >= 40 ? "text-yellow-400" : "text-red-400"}`}>{overallPct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Season Info */}
        <div className="mb-6 text-center">
          <h2 className="font-anton text-xl text-red-500 mb-1">
            {seriesFilter === "the-red-ink-chronicles" ? "THE RED INK CHRONICLES" : seriesFilter === "the-notarys-shadow" ? "THE NOTARY'S SHADOW" : "SIGNED, SEALED, BETRAYED"} — SEASON {seasonFilter}
          </h2>
          <p className="text-gray-500">{episodes.length} episodes loaded</p>
        </div>

        {/* ==================== REVISION SCANNER ==================== */}
        <div className="mb-6 p-4 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="revision-scanner">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              <h3 className="font-anton text-sm tracking-wider">STORY REVISION SCANNER</h3>
            </div>
            <button onClick={runRevisionScan} disabled={scanning}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors rounded disabled:opacity-50"
              data-testid="run-revision-scan-btn">
              {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {scanning ? "Scanning..." : `Scan Season ${seasonFilter}`}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">Analyzes stories for quality issues: word count, missing SFX cues, scene descriptions, and dialogue.</p>

          {revisionScan && showRevisionPanel && (
            <div className="mt-3 space-y-3" data-testid="revision-results">
              <div className="flex flex-wrap gap-3">
                <div className="px-3 py-2 bg-white/5 rounded text-center">
                  <p className="text-lg font-bold">{revisionScan.summary.total_scanned}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Scanned</p>
                </div>
                <div className={`px-3 py-2 rounded text-center ${(revisionScan.summary.story_issues || 0) > 0 ? "bg-red-900/30 border border-red-500/30" : "bg-green-900/30 border border-green-500/30"}`}>
                  <p className="text-lg font-bold">{revisionScan.summary.story_issues || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Need Scripts</p>
                </div>
                <div className={`px-3 py-2 rounded text-center bg-amber-900/20 border border-amber-500/20`}>
                  <p className="text-lg font-bold">{revisionScan.summary.asset_issues || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Missing Assets</p>
                </div>
                {Object.entries(revisionScan.summary.issues).filter(([key]) => !["missing_thumbnail", "missing_logline"].includes(key)).map(([key, count]) => count > 0 && (
                  <div key={key} className="px-3 py-2 bg-red-900/20 border border-red-500/20 rounded text-center">
                    <p className="text-lg font-bold text-red-400">{count}</p>
                    <p className="text-[10px] text-gray-500 uppercase">{key.replace(/_/g, " ")}</p>
                  </div>
                ))}
              </div>
              {(revisionScan.summary.story_issues || 0) === 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-500/30 rounded">
                  <CheckCircle size={18} className="text-green-400" />
                  <p className="text-sm text-green-300">All stories in Season {seasonFilter} have scripts! Remaining issues are asset-related (thumbnails, loglines).</p>
                </div>
              )}
              {revisionScan.flagged_episodes.filter(ep => ep.has_story_issues).length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">{revisionScan.flagged_episodes.filter(ep => ep.has_story_issues).length} episodes need story generation</p>
                    <button onClick={batchReviseAll} disabled={batchRevising || revisingId}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
                      data-testid="batch-revise-all-btn">
                      {batchRevising ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                      {batchRevising
                        ? `Writing ${batchReviseProgress.done}/${batchReviseProgress.total} (${batchReviseProgress.saved || 0} saved)...`
                        : `Write All ${revisionScan.flagged_episodes.filter(ep => ep.has_story_issues).length} Scripts`}
                    </button>
                  </div>
                  {batchRevising && (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={14} className="text-amber-400 animate-spin" />
                        <span className="text-amber-400 text-xs font-bold">Writing scripts in background — you can navigate away</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-500 rounded-full transition-all duration-700"
                            style={{ width: `${batchReviseProgress.total > 0 ? (batchReviseProgress.done / batchReviseProgress.total) * 100 : 0}%` }} />
                        </div>
                        <span className="text-amber-400 text-[10px] font-mono">{batchReviseProgress.done}/{batchReviseProgress.total}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        {batchReviseProgress.current && (
                          <p className="text-gray-500 text-[10px] truncate">Writing: {batchReviseProgress.current}</p>
                        )}
                        {(batchReviseProgress.saved || 0) > 0 && (
                          <p className="text-green-400 text-[10px] font-bold">{batchReviseProgress.saved} saved to DB</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
                    {revisionScan.flagged_episodes.filter(ep => ep.has_story_issues).map(ep => (
                      <div key={ep.id} className="p-3 bg-[#121212] border border-white/5 rounded-lg" data-testid={`flagged-episode-${ep.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-xs text-gray-500 mr-2">S{ep.season}E{ep.episode_number}</span>
                            <span className="text-sm font-semibold">{ep.title}</span>
                            <span className="text-xs text-gray-500 ml-2">({ep.word_count} words)</span>
                          </div>
                          <button onClick={() => reviseEpisode(ep.id)} disabled={revisingId === ep.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
                            data-testid={`revise-btn-${ep.id}`}>
                            {revisingId === ep.id ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                            {revisingId === ep.id ? "Writing..." : "AI Write"}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {ep.issues.filter(i => i.category === "story").map((issue, idx) => (
                            <span key={idx} className="text-[10px] px-2 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 rounded">{issue.detail}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ==================== EXTRACTED COMPONENTS ==================== */}
        <details className="mb-6 border border-white/10 rounded-lg bg-white/[0.02]">
          <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-gray-400 hover:text-white transition-colors">
            <Volume2 size={16} className="text-purple-500" />
            <span className="font-anton text-sm tracking-wider">MUSIC MANAGER</span>
            <span className="text-gray-600 text-xs ml-2">Click to expand</span>
          </summary>
          <div className="px-2 pb-2">
            <MusicManager />
          </div>
        </details>
        <VoiceProfileManager seriesFilter={seriesFilter} />

        {/* ==================== EXPANSION STATUS BANNER ==================== */}
        {expansionStatus && (
          <div className="mb-6 p-4 bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-500/30 rounded-lg" data-testid="expansion-banner">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Zap className="text-yellow-400" size={24} />
                <div>
                  <p className="font-semibold">Story Expansion Progress</p>
                  <p className="text-sm text-gray-400">{expansionStatus.expanded} of {expansionStatus.total_episodes} episodes expanded ({expansionStatus.percent_complete}%)</p>
                </div>
              </div>
              {!expanding && expansionStatus.pending > 0 && (
                <button onClick={continueAllExpansions} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors" data-testid="continue-expansions-btn">
                  <Zap size={18} /> Expand All ({expansionStatus.pending} remaining)
                </button>
              )}
              {expanding && (
                <div className="flex items-center gap-3"><RefreshCw size={18} className="animate-spin text-green-400" /><span className="text-green-400 text-sm">Expanding...</span></div>
              )}
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-500" style={{ width: `${expansionStatus.percent_complete}%` }} />
            </div>
            {expanding && currentExpanding && <p className="mt-2 text-sm text-gray-400">Currently expanding: {currentExpanding} ({expandProgress.done}/{expandProgress.total})</p>}
            {expansionStatus.word_stats && expansionStatus.word_stats.total > 0 && (
              <p className="mt-2 text-xs text-gray-500">Total words: {expansionStatus.word_stats.total.toLocaleString()} | Avg: {expansionStatus.word_stats.average} words | ~{Math.round(expansionStatus.word_stats.average / 150)} min read each</p>
            )}
          </div>
        )}

        {/* ==================== THUMBNAIL GENERATION BANNER ==================== */}
        {thumbnailStatus && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg" data-testid="thumbnail-banner">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Image className="text-purple-400" size={24} />
                <div>
                  <p className="font-semibold">AI Thumbnail Generation (All Seasons)</p>
                  <p className="text-sm text-gray-400">{thumbnailStatus.with_thumbnails} of {thumbnailStatus.total_episodes} episodes have thumbnails ({thumbnailStatus.percent_complete}%)</p>
                </div>
              </div>
              {!generatingThumbnails && thumbnailStatus.without_thumbnails > 0 && (
                <button onClick={generateAllThumbnails} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors" data-testid="generate-thumbnails-btn">
                  <Image size={18} /> Generate Thumbnails ({thumbnailStatus.without_thumbnails} remaining)
                </button>
              )}
              {generatingThumbnails && (
                <div className="flex items-center gap-3"><RefreshCw size={18} className="animate-spin text-purple-400" /><span className="text-purple-400 text-sm">Generating...</span></div>
              )}
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500" style={{ width: `${thumbnailStatus.percent_complete}%` }} />
            </div>
            {generatingThumbnails && currentThumbnail && <p className="mt-2 text-sm text-gray-400">Currently generating: {currentThumbnail} ({thumbnailProgress.done}/{thumbnailProgress.total})</p>}
          </div>
        )}

        {/* ==================== STATS ==================== */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8" data-testid="episode-stats">
          <div className="bg-white/5 p-4 rounded-lg text-center"><p className="text-2xl font-bold">{episodes.length}</p><p className="text-xs text-gray-500">Season {seasonFilter} Episodes</p></div>
          <div className="bg-white/5 p-4 rounded-lg text-center"><p className="text-2xl font-bold text-green-500">{stats.expanded}</p><p className="text-xs text-gray-500">With Scripts</p></div>
          <div className="bg-white/5 p-4 rounded-lg text-center">
            <p className="text-2xl font-bold text-emerald-500">
              {filteredEpisodes.filter(ep => { const s = ep.full_story || ""; const wc = s.split(/\s+/).filter(Boolean).length; return wc >= 500 && /\[(\*{0,2})sfx:/i.test(s) && /^[A-Z\*].*:/m.test(s); }).length}
            </p>
            <p className="text-xs text-gray-500">Scripts Ready</p>
          </div>
          <div className="bg-white/5 p-4 rounded-lg text-center"><p className="text-2xl font-bold text-yellow-500">{episodes.length - stats.expanded}</p><p className="text-xs text-gray-500">Need Scripts</p></div>
          <div className="bg-white/5 p-4 rounded-lg text-center"><p className="text-2xl font-bold text-blue-500">{stats.withAssets}</p><p className="text-xs text-gray-500">With Audio</p></div>
          <div className="bg-white/5 p-4 rounded-lg text-center"><p className="text-2xl font-bold text-purple-500">{stats.withThumbnails}</p><p className="text-xs text-gray-500">With Thumbnails</p></div>
        </div>

        {/* ==================== SFX CUE OVERVIEW ==================== */}
        <div className="mb-6 p-4 border border-amber-500/20 rounded-lg bg-amber-900/5" data-testid="sfx-overview-section">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setShowSfxPanel(!showSfxPanel)} className="flex items-center gap-2">
              {showSfxPanel ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
              <Volume2 size={18} className="text-amber-500" />
              <h3 className="font-anton text-sm tracking-wider">SFX CUE SHEET — Season {seasonFilter}</h3>
            </button>
            <button
              onClick={async () => {
                setSfxLoading(true);
                try {
                  const res = await axios.get(`${API}/episodes/sfx-cues/season?series=${seriesFilter}&season=${seasonFilter}`);
                  setSfxData(res.data);
                  setShowSfxPanel(true);
                } catch (e) { console.error(e); }
                setSfxLoading(false);
              }}
              disabled={sfxLoading}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
              data-testid="load-sfx-btn">
              {sfxLoading ? <RefreshCw size={12} className="animate-spin" /> : <Volume2 size={12} />}
              {sfxLoading ? "Scanning..." : "Extract SFX Cues"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">Extracts all [SFX: ...] sound cues from scripts into a production-ready checklist.</p>
          {showSfxPanel && sfxData && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                <div className="px-3 py-2 bg-white/5 rounded text-center">
                  <p className="text-lg font-bold text-amber-400">{sfxData.total_cues}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Total Cues</p>
                </div>
                <div className="px-3 py-2 bg-white/5 rounded text-center">
                  <p className="text-lg font-bold text-amber-300">{sfxData.unique_cues}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Unique Sounds</p>
                </div>
                <div className="px-3 py-2 bg-white/5 rounded text-center">
                  <p className="text-lg font-bold">{sfxData.total_episodes}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Episodes With Scripts</p>
                </div>
              </div>
              {sfxData.cues.length > 0 && (
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0a0a0a]">
                      <tr className="text-gray-500 uppercase border-b border-white/10">
                        <th className="text-left py-2 px-2">Sound Effect</th>
                        <th className="text-center py-2 px-2 w-16">Uses</th>
                        <th className="text-left py-2 px-2 w-32">In Episodes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sfxData.cues.map((cue, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-1.5 px-2 text-gray-300">{cue.description}</td>
                          <td className="py-1.5 px-2 text-center text-amber-400">{cue.count}</td>
                          <td className="py-1.5 px-2 text-gray-500 text-[10px]">E{cue.episodes.join(", E")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sfxData.cues.length === 0 && (
                <p className="text-center text-gray-500 py-4">No SFX cues found in Season {seasonFilter} scripts. Scripts may need [SFX: description] formatted cues.</p>
              )}
            </div>
          )}
        </div>

        {/* ==================== SFX LIBRARY SCRAPER ==================== */}
        <div className="mb-6 p-4 border border-emerald-500/20 rounded-lg bg-emerald-900/5" data-testid="sfx-scraper-section">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setShowSfxScraper(!showSfxScraper)} className="flex items-center gap-2">
              {showSfxScraper ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
              <Download size={18} className="text-emerald-500" />
              <h3 className="font-anton text-sm tracking-wider">SFX LIBRARY DOWNLOADER</h3>
            </button>
            <button
              onClick={async () => {
                if (scrapeRunning) return;
                setScrapeRunning(true);
                try {
                  const res = await axios.post(`${API}/sfx/scrape`, { sounds_per_category: 5 });
                  const jobId = res.data.job_id;
                  const poll = setInterval(async () => {
                    try {
                      const statusRes = await axios.get(`${API}/sfx/scrape/${jobId}`);
                      setScrapeJob(statusRes.data);
                      if (statusRes.data.status !== "running") {
                        clearInterval(poll);
                        setScrapeRunning(false);
                        const dlRes = await axios.get(`${API}/sfx/downloads`);
                        setSfxDownloads(dlRes.data.uploads || []);
                      }
                    } catch { clearInterval(poll); setScrapeRunning(false); }
                  }, 2000);
                } catch (e) {
                  console.error(e);
                  setScrapeRunning(false);
                }
              }}
              disabled={scrapeRunning}
              className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
              data-testid="scrape-sfx-btn">
              {scrapeRunning ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              {scrapeRunning ? "Downloading..." : "Download from Mixkit"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">Auto-downloads free SFX from Mixkit.co across all 15 categories and matches them to your episode cues.</p>

          {scrapeJob && (
            <div className="mb-3 p-3 bg-white/5 rounded-lg">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className={`font-semibold ${scrapeJob.status === "complete" ? "text-emerald-400" : scrapeJob.status === "error" ? "text-red-400" : "text-amber-400"}`}>
                  {scrapeJob.status === "complete" ? "Complete" : scrapeJob.status === "error" ? "Error" : `Scraping: ${scrapeJob.current_category}...`}
                </span>
                <span className="text-gray-400">{scrapeJob.downloaded} sounds / {scrapeJob.matched_cues} cues matched</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-1.5">
                <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${(scrapeJob.completed_categories?.length / (scrapeJob.categories?.length || 1)) * 100}%` }} />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {scrapeJob.categories?.map(cat => (
                  <span key={cat} className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                    scrapeJob.completed_categories?.includes(cat) ? "bg-emerald-500/20 text-emerald-400"
                    : scrapeJob.current_category === cat ? "bg-amber-500/20 text-amber-400"
                    : "bg-white/5 text-gray-500"
                  }`}>{cat}</span>
                ))}
              </div>
            </div>
          )}

          {showSfxScraper && (
            <div>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/sfx/downloads`);
                    setSfxDownloads(res.data.uploads || []);
                  } catch (e) { console.error(e); }
                }}
                className="mb-2 text-xs text-emerald-400 hover:text-emerald-300 underline"
                data-testid="refresh-downloads-btn">
                Refresh Downloads
              </button>
              {sfxDownloads.length > 0 ? (
                <div className="max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#0a0a0a]">
                      <tr className="text-gray-500 uppercase border-b border-white/10">
                        <th className="text-left py-2 px-2">Sound</th>
                        <th className="text-left py-2 px-2 w-24">Category</th>
                        <th className="text-left py-2 px-2 w-20">Source</th>
                        <th className="text-center py-2 px-2 w-16">Play</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sfxDownloads.map((dl, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-1.5 px-2 text-gray-300">{dl.display_name}</td>
                          <td className="py-1.5 px-2"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px]">{dl.category}</span></td>
                          <td className="py-1.5 px-2 text-gray-500">{dl.source}</td>
                          <td className="py-1.5 px-2 text-center">
                            <button onClick={() => {
                              const audio = new Audio(`${BACKEND_URL}${dl.audio_url}`);
                              audio.play();
                            }} className="text-emerald-400 hover:text-emerald-300">
                              <Volume2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-3 text-xs">No downloads yet. Click "Download from Mixkit" to populate the library.</p>
              )}
            </div>
          )}
        </div>

        {/* ==================== VOICE LAB ==================== */}
        <details className="mb-6 border border-red-500/20 rounded-lg bg-red-900/5" data-testid="voice-lab-section">
          <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-gray-400 hover:text-white transition-colors">
            <Zap size={16} className="text-red-500" />
            <span className="font-anton text-sm tracking-wider">VOICE LAB</span>
            <span className="text-gray-600 text-xs ml-2">Create dialect profiles, preview voices, assign to characters</span>
          </summary>
          <div className="px-4 pb-4">
            <VoiceLab series={seriesFilter} />
          </div>
        </details>

        {/* ==================== SFX PREVIEW PLAYER ==================== */}
        <details className="mb-6 border border-amber-500/20 rounded-lg bg-amber-900/5" data-testid="sfx-preview-section">
          <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-gray-400 hover:text-white transition-colors">
            <Volume2 size={16} className="text-amber-500" />
            <span className="font-anton text-sm tracking-wider">SFX PREVIEW PLAYER</span>
            <span className="text-gray-600 text-xs ml-2">Audition & swap sounds per cue</span>
          </summary>
          <div className="px-4 pb-4">
            <SfxPreviewPlayer seriesFilter={seriesFilter} />
          </div>
        </details>

        {/* ==================== BATCH TTS GENERATOR ==================== */}
        <div className="mb-6 p-4 border border-cyan-500/20 rounded-lg bg-cyan-900/5" data-testid="batch-tts-section">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setShowBatchTTS(!showBatchTTS)} className="flex items-center gap-2">
              {showBatchTTS ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
              <Volume2 size={18} className="text-cyan-500" />
              <h3 className="font-anton text-sm tracking-wider">BATCH TTS GENERATOR</h3>
            </button>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/tts/batch`, {
                      series: seriesFilter, season: parseInt(seasonFilter), skip_existing: true
                    });
                    setBatchTTSRunning(true);
                    const pollAll = setInterval(async () => {
                      try {
                        const statusRes = await axios.get(`${API}/tts/batch/status`);
                        setBatchTTSJob(statusRes.data);
                        const anyRunning = statusRes.data.some(j => j.status === "running" || j.status === "queued");
                        if (!anyRunning) { clearInterval(pollAll); setBatchTTSRunning(false); }
                      } catch { clearInterval(pollAll); setBatchTTSRunning(false); }
                    }, 3000);
                  } catch (e) { console.error(e); }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold rounded transition-colors"
                data-testid="batch-tts-btn">
                <Zap size={12} /> Season {seasonFilter}
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/tts/batch-all`);
                    setBatchTTSRunning(true);
                    const pollAll = setInterval(async () => {
                      try {
                        const statusRes = await axios.get(`${API}/tts/batch/status`);
                        setBatchTTSJob(statusRes.data);
                        const anyRunning = statusRes.data.some(j => j.status === "running" || j.status === "queued");
                        if (!anyRunning) { clearInterval(pollAll); setBatchTTSRunning(false); }
                      } catch { clearInterval(pollAll); setBatchTTSRunning(false); }
                    }, 3000);
                  } catch (e) { console.error(e); }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded transition-colors"
                data-testid="batch-tts-all-btn">
                <Zap size={12} /> All Series
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">Generate multi-voice TTS audio using assigned character voices. Jobs queue automatically.</p>

          {batchTTSJob && (Array.isArray(batchTTSJob) ? batchTTSJob : [batchTTSJob]).map((job, i) => (
            <div key={job.id || i} className="mb-2 p-3 bg-white/5 rounded-lg">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="font-semibold text-gray-300">{job.label}</span>
                <span className={`font-semibold ${
                  job.status === "complete" ? "text-cyan-400" : job.status === "error" ? "text-red-400"
                  : job.status === "queued" ? "text-gray-500" : "text-amber-400"
                }`}>
                  {job.status === "complete" ? "Complete" : job.status === "error" ? "Error"
                  : job.status === "queued" ? "Queued"
                  : `S${job.current_season}E${job.current_ep_num}: ${job.current_episode}`}
                </span>
              </div>
              {job.status === "running" && (
                <div className="w-full bg-white/10 rounded-full h-1.5 mb-1">
                  <div className="bg-cyan-500 h-1.5 rounded-full transition-all" style={{ width: `${job.total_episodes ? (job.completed / job.total_episodes) * 100 : 0}%` }} />
                </div>
              )}
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span>{job.completed}/{job.total_episodes} episodes{job.failed > 0 ? ` (${job.failed} failed)` : ""}</span>
                {(job.status === "running" || job.status === "queued") && (
                  <button onClick={async () => { try { await axios.post(`${API}/tts/batch/${job.id}/cancel`); } catch {} }}
                    className="text-red-400 hover:text-red-300 underline" data-testid={`cancel-tts-${job.id}`}>Cancel</button>
                )}
              </div>
            </div>
          ))}

          {showBatchTTS && (
            <div className="mt-3 p-3 bg-white/[0.03] rounded-lg text-xs text-gray-400 space-y-1">
              <p>Uses character voices from the Character Editor.</p>
              <p>Skips episodes that already have generated TTS audio.</p>
              <p>Jobs queue sequentially — one series at a time to manage API usage.</p>
            </div>
          )}
        </div>

        {/* ==================== AUDIO MIXER / COMPOSER ==================== */}
        <AudioMixerSection seriesFilter={seriesFilter} seasonFilter={seasonFilter} />

        {/* Bulk Export */}
        <div className="mb-6 flex gap-4" data-testid="bulk-export">
          <button onClick={bulkExportStories} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors" data-testid="bulk-export-btn">
            <Download size={18} /> Export Full Stories ({filteredEpisodes.filter(ep => ep.full_story).length} episodes)
          </button>
          <button onClick={fetchExpansionStatus} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors">
            <RefreshCw size={18} /> Refresh Status
          </button>
        </div>

        {/* ==================== VOICE ASSIGNMENTS SECTION ==================== */}
        <div className="mb-6 p-4 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="voice-assign-section">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setVoiceAssignCollapsed(!voiceAssignCollapsed)} className="flex items-center gap-2">
              {voiceAssignCollapsed ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
              <Volume2 size={18} className="text-cyan-500" />
              <h3 className="font-anton text-sm tracking-wider">VOICE ASSIGNMENTS</h3>
            </button>
            {!voiceAssignCollapsed && (
              <button onClick={async () => {
                const season = parseInt(seasonFilter);
                if (!window.confirm(`Auto-assign voice profiles to all Season ${season} episodes?`)) return;
                setBatchAssigning(true);
                try {
                  const res = await axios.post(`${API}/episodes/batch-voice-assign`, { season, series: seriesFilter });
                  alert(`Auto-assigned voices to ${res.data.assigned} episodes!`);
                } catch (e) { alert("Batch assign failed"); }
                finally { setBatchAssigning(false); }
              }} disabled={batchAssigning}
                className="px-3 py-1.5 text-[11px] font-bold bg-cyan-600/20 text-cyan-400 border border-cyan-500/20 rounded hover:bg-cyan-600/30 transition-all"
                data-testid="batch-voice-assign-btn">
                {batchAssigning ? "Assigning..." : `Auto-Assign Season ${seasonFilter}`}
              </button>
            )}
          </div>
          {!voiceAssignCollapsed && (
            <>
              <p className="text-xs text-gray-500 mb-3 ml-8">Assign voice profiles to characters for audio production. Select an episode below to configure.</p>
              <div className="space-y-1 max-h-[300px] overflow-y-auto mb-3">
                {episodes.slice(0, 50).map(ep => {
                  const isActive = voiceAssignEpId === ep.id;
                  return (
                    <button key={ep.id} onClick={() => setVoiceAssignEpId(isActive ? null : ep.id)}
                      className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-3 transition-all ${isActive ? "bg-cyan-950/30 border border-cyan-500/30" : "hover:bg-white/[0.03] border border-transparent"}`}
                      data-testid={`voice-ep-${ep.id}`}>
                      <span className="text-[10px] text-gray-600 font-mono w-10 flex-shrink-0">E{ep.episode_number}</span>
                      <span className="flex-1 truncate">{ep.title}</span>
                      {isActive && <ChevronUp size={14} className="text-cyan-400" />}
                    </button>
                  );
                })}
              </div>
              {voiceAssignEpId && (
                <VoiceAssignmentPanel episodeId={voiceAssignEpId} seriesFilter={seriesFilter} onClose={() => setVoiceAssignEpId(null)} />
              )}
            </>
          )}
        </div>

        {/* ==================== EPISODES LIST ==================== */}
        <div className="mb-6 p-4 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="episodes-section">
          <button onClick={() => setEpisodesCollapsed(!episodesCollapsed)} className="flex items-center gap-2 mb-1 w-full">
            {episodesCollapsed ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
            <FileText size={18} className="text-red-500" />
            <h3 className="font-anton text-sm tracking-wider">EPISODES</h3>
            <span className="text-xs text-gray-500 ml-1">{episodes.length} loaded</span>
          </button>
          {!episodesCollapsed && (
            <>
              <div className="flex flex-wrap gap-4 mb-6 mt-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input type="text" placeholder="Search episodes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-[#1a1a1a] border border-white/15 rounded text-white focus:outline-none focus:border-red-500"
                      data-testid="search-episodes" />
                  </div>
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 bg-[#1a1a1a] border border-white/15 rounded text-white focus:outline-none focus:border-red-500"
                  data-testid="status-filter">
                  <option value="all">All Statuses</option>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              {episodes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No episodes found for Season {seasonFilter}. Try a different season above.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredEpisodes.map(episode => (
                    <EpisodeAssetCard key={episode.id} episode={episode} onUpdate={fetchEpisodes} />
                  ))}
                  {filteredEpisodes.length === 0 && episodes.length > 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Filter size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No episodes match your search/filter. Try clearing the search or changing the status filter.</p>
                      <button onClick={() => { setSearchTerm(""); setStatusFilter("all"); }} className="mt-3 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 rounded transition-colors">
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetManager;
